import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserInfo } from '../types';
import { useFilteredReports } from '../hooks/useFilteredReports';
import { BillingStatusBadge } from '../components/BillingStatusBadge';
import { BillingStatusLegendTooltip } from '../components/BillingStatusLegendTooltip';
import { Pagination } from '../components/Pagination';
import { CurrentUserInfo } from '../components/sidebar';
import { authService } from '../services/authService';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { LanguageSelector } from '../components/LanguageSelector';
import { safeFormatDate } from '../utils/dateUtils';
import { getDashboardPath } from '../utils/navigationUtils';
import './FinanceHomePage.css';

/**
 * T-017: home page dedicata al ruolo FINANCE. Di default mostra i soli report CLOSED; filtro per
 * stato di fatturazione e ricerca per riferimento commerciale + numero report (non il set completo
 * di filtri della dashboard generica). Il click sulla riga apre direttamente /report/:id/billing.
 */
const FINANCE_FILTER_SESSION_KEY = 'proservice_finance_filters';

const financeInitialFilterOverrides = {
  showNew: false,
  showReady: false,
  showInProgress: false,
  showSigned: false,
  showClosed: true,
  showCanceled: false,
};

const FinanceHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reports, loading, filter, setFilter, page, pageSize, totalPages, totalElements, setPage, setPageSize } =
    useFilteredReports(financeInitialFilterOverrides, FINANCE_FILTER_SESSION_KEY);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) setCurrentUser(user);
  }, []);

  const handleOpenBilling = (reportId: string): void => {
    // Cosi' ReportBillingPage sa costruire la breadcrumb di ritorno verso questa lista invece
    // che verso la dashboard standard (rilevante per ADMIN/SUPER_ADMIN, che hanno entrambe).
    navigate(`/report/${reportId}/billing`, { state: { from: 'billing-list' } });
  };

  const handleLogout = (): void => {
    authService.logout();
    navigate('/login');
  };

  const breadcrumbItems: BreadcrumbItem[] = [{ label: '', translateKey: 'breadcrumb.financeHome' }];

  // T-017: ADMIN/SUPER_ADMIN raggiungono questa pagina dalla dashboard standard (loro home
  // naturale) tramite il pulsante Fatturazione - devono poter tornare indietro allo stesso modo.
  // FINANCE ha questa pagina come home ma puo' aprire anche la dashboard tecnica (su /dashboard).
  const canAccessDashboard =
    currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'FINANCE';

  return (
    <div className="finance-home-page">
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          {canAccessDashboard && (
            <button
              type="button"
              className="btn-primary btn-billing"
              onClick={() => navigate(getDashboardPath(currentUser))}
            >
              <span className="material-icons">dashboard</span>
              {t('breadcrumb.dashboard')}
            </button>
          )}
          <LanguageSelector />
        </div>
      </FormHeader>

      <div className="main-layout">
        <div className="main-content">
          <div className="filters-section">
          <fieldset className="search-filters">
            <legend>{t('mainForm.searchFilters')}</legend>
            <div className="search-filters-group">
              <input
                type="text"
                placeholder={t('reportTable.number')}
                value={filter.reportNumber || ''}
                onChange={(e) => setFilter({ ...filter, reportNumber: e.target.value })}
                className="filter-input"
                aria-label={t('mainForm.filterByReportNumber')}
              />
              <input
                type="text"
                placeholder={t('billing.commercialReference')}
                value={filter.commercialReference || ''}
                onChange={(e) => setFilter({ ...filter, commercialReference: e.target.value })}
                className="filter-input"
                aria-label={t('billing.commercialReference')}
              />
              <input
                type="text"
                placeholder={t('reportTable.customer')}
                value={filter.customerName || ''}
                onChange={(e) => setFilter({ ...filter, customerName: e.target.value })}
                className="filter-input"
                aria-label={t('mainForm.filterByCustomerName')}
              />
              <input
                type="text"
                placeholder={t('reportTable.technician')}
                value={filter.technicianName || ''}
                onChange={(e) => setFilter({ ...filter, technicianName: e.target.value })}
                className="filter-input"
                aria-label={t('mainForm.filterByTechnician')}
              />
            </div>
          </fieldset>

          <fieldset className="status-filters">
            <legend>
              {t('billing.status.title')}
              <BillingStatusLegendTooltip />
            </legend>
            <div className="status-toggle-group billing-status-toggle-group">
              <button
                type="button"
                className={`status-toggle billing-status-toggle-to-validate ${filter.showBillingToValidate ? 'active' : ''}`}
                onClick={() => setFilter({ ...filter, showBillingToValidate: !filter.showBillingToValidate })}
                aria-label={t('billing.status.TO_VALIDATE')}
                aria-pressed={filter.showBillingToValidate}
              >
                {t('billing.status.TO_VALIDATE')}
              </button>
              <button
                type="button"
                className={`status-toggle billing-status-toggle-validated ${filter.showBillingValidated ? 'active' : ''}`}
                onClick={() => setFilter({ ...filter, showBillingValidated: !filter.showBillingValidated })}
                aria-label={t('billing.status.VALIDATED')}
                aria-pressed={filter.showBillingValidated}
              >
                {t('billing.status.VALIDATED')}
              </button>
              <button
                type="button"
                className={`status-toggle billing-status-toggle-invoiced ${filter.showBillingInvoiced ? 'active' : ''}`}
                onClick={() => setFilter({ ...filter, showBillingInvoiced: !filter.showBillingInvoiced })}
                aria-label={t('billing.status.INVOICED')}
                aria-pressed={filter.showBillingInvoiced}
              >
                {t('billing.status.INVOICED')}
              </button>
              <button
                type="button"
                className={`status-toggle billing-status-toggle-none ${filter.showBillingNone ? 'active' : ''}`}
                onClick={() => setFilter({ ...filter, showBillingNone: !filter.showBillingNone })}
                aria-label={t('billing.status.NONE')}
                aria-pressed={filter.showBillingNone}
              >
                {t('billing.status.NONE')}
              </button>
            </div>
          </fieldset>
          </div>

          {loading && <div className="loading">{t('app.loading')}</div>}

          {!loading && (
            <>
              <div className="reports-table-container">
                <table className="reports-table finance-reports-table">
                  <thead>
                    <tr>
                      <th>{t('reportTable.number')}</th>
                      <th>{t('billing.status.title')}</th>
                      <th>{t('reportTable.customer')}</th>
                      <th>{t('reportTable.technician')}</th>
                      <th>{t('reportTable.startDate')}</th>
                      <th>{t('billing.commercialReference')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="no-results">{t('mainForm.noResults')}</td>
                      </tr>
                    ) : (
                      reports.map((report) => (
                        <tr key={report.id} onClick={() => handleOpenBilling(report.id)} style={{ cursor: 'pointer' }}>
                          <td data-label={t('reportTable.number')}>{report.number}</td>
                          <td data-label={t('billing.status.title')}>
                            <BillingStatusBadge billingStatus={report.billingStatus} />
                          </td>
                          <td data-label={t('reportTable.customer')}>{report.customerName || '-'}</td>
                          <td data-label={t('reportTable.technician')}>{report.technicianName || '-'}</td>
                          <td data-label={t('reportTable.startDate')}>{safeFormatDate(report.startTime, 'dd/MM/yyyy')}</td>
                          <td data-label={t('billing.commercialReference')}>{report.commercialReference || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalElements}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </div>

        <aside className="user-sidebar">
          <CurrentUserInfo user={currentUser} agent={null} onLogout={handleLogout} />
        </aside>
      </div>
    </div>
  );
};

export default FinanceHomePage;
